import { useCallback, useEffect, useRef } from 'react';
// @ts-expect-error untyped JS hook
import { useSignalRListener } from './useSignalR';
// @ts-expect-error untyped JS module
import { queryClient } from '../config/queryClient';
// @ts-expect-error untyped JS const map
import { SIGNALR_LISTENERS } from '../config/signalr';
import { useWorkflowChrome } from '../components/Layout/WorkflowChromeContext';

// 250ms coalescing window. SignalR can emit several status events in close
// succession when a single backend transition flips multiple fields (e.g.
// extraction completes → output_table starts → period record touched).
// Without coalescing each event would invalidate the period query and trigger
// a refetch. 250ms is small enough to feel instantaneous while comfortably
// swallowing event bursts.
const COALESCE_MS = 250;

interface WorkflowStatusPayload {
  environment?: string;
  workflow_id?: string;
  period_id?: string;
  [key: string]: unknown;
}

/**
 * Listens to every workflow-progress SignalR event and refreshes the period
 * query so the stepper updates in near-real-time. Replaces the periodic
 * polling that previously lived inside `useWorkflowStages`.
 *
 * Designed to be mounted once at the chrome level (inside
 * `WorkflowChromeProvider`) — every page that consumes the stepper benefits
 * without each page wiring its own listener.
 *
 * Invalidates **every** `GET_PERIOD_BY_ID` variant for the current period
 * (including the FCA/ESMA/FormPF Report containers' `customKey` variants)
 * via a predicate-form `invalidateQueries`. `GET_WORKFLOW` is also
 * invalidated on `WorkflowPeriodUpdate` so the AppTopBar period chips
 * refresh when the workflow's periods array changes.
 */
export const useWorkflowChromeStatusSync = (): void => {
  const ctx = useWorkflowChrome();
  const workflowId = ctx.workflowId;
  const periodId = ctx.periodId;

  // Stable refs so the per-event handlers can be defined once outside React's
  // render cycle while still seeing the latest ids on each event.
  const workflowIdRef = useRef<string | undefined>(workflowId);
  const periodIdRef = useRef<string | undefined>(periodId);
  useEffect(() => {
    workflowIdRef.current = workflowId;
    periodIdRef.current = periodId;
  });

  // Coalesced invalidate. We don't want N invalidations when a single
  // backend transition emits 2-3 events.
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushInvalidations = useCallback(() => {
    const currentPeriodId = periodIdRef.current;
    if (!currentPeriodId) return;

    let matched = 0;
    queryClient.invalidateQueries({
      predicate: (query: { queryKey: unknown[] }) => {
        const key = query.queryKey;
        if (!Array.isArray(key)) return false;
        const prefix = key[0];
        // Period record — keyed by [prefix, customKey, periodId].
        if (prefix === 'GET_PERIOD_BY_ID' && key[2] === currentPeriodId) {
          matched++;
          return true;
        }
        // Live extraction / transformation stats used by the stepper's
        // "X/Y extracted" fraction — keyed by [prefix, periodId, params].
        if (
          (prefix === 'GET_EXTRACTION_STATS' || prefix === 'GET_TRANSFORMATION_STATS') &&
          key[1] === currentPeriodId
        ) {
          matched++;
          return true;
        }
        return false;
      }
    });
    if (import.meta.env.DEV) {
      console.log(
        `[WorkflowChromeStatusSync] Invalidated ${matched} queries for period ${currentPeriodId}`
      );
    }
  }, []);

  const scheduleInvalidate = useCallback(() => {
    if (pendingTimerRef.current != null) return;
    pendingTimerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;
      flushInvalidations();
    }, COALESCE_MS);
  }, [flushInvalidations]);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current != null) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, []);

  // Permissive matcher — mirrors the Quarters dashboard's
  // `matchPeriodInThisWorkflow` so both surfaces behave identically.
  // Rule: DROP only when we have strong evidence of mismatch (an ID field
  // is present AND clearly points elsewhere). Missing IDs count as "could
  // be for us" and pass through, so batch backend emissions that omit
  // `period_id` still trigger a refresh — this was the source of the
  // stepper's laggy / stuck-status behaviour.
  const matchesCurrentContext = useCallback((payload?: WorkflowStatusPayload): boolean => {
    const currentPeriodId = periodIdRef.current;
    const currentWorkflowId = workflowIdRef.current;
    if (!currentPeriodId) return false;
    if (payload?.workflow_id && payload.workflow_id !== currentWorkflowId) return false;
    if (payload?.period_id && payload.period_id !== currentPeriodId) return false;
    return true;
  }, []);

  const handlePeriodScopedEvent = useCallback(
    (payload?: WorkflowStatusPayload) => {
      if (import.meta.env.DEV) {
        console.log('[WorkflowChromeStatusSync] SignalR event received', {
          payload_period_id: payload?.period_id,
          payload_workflow_id: payload?.workflow_id,
          current_period_id: periodIdRef.current,
          current_workflow_id: workflowIdRef.current,
          matches: matchesCurrentContext(payload)
        });
      }
      if (!matchesCurrentContext(payload)) return;
      scheduleInvalidate();
    },
    [matchesCurrentContext, scheduleInvalidate]
  );

  const handleWorkflowPeriodEvent = useCallback(
    (payload?: WorkflowStatusPayload) => {
      const currentPeriodId = periodIdRef.current;
      const currentWorkflowId = workflowIdRef.current;
      if (!currentPeriodId) return;

      const periodMatches = !!payload?.period_id && payload.period_id === currentPeriodId;
      const workflowMatches =
        !payload?.period_id && !!payload?.workflow_id && payload.workflow_id === currentWorkflowId;
      if (!periodMatches && !workflowMatches) return;

      // Period chips in the AppTopBar carry workflow-level status — refresh
      // the workflow record alongside the period.
      queryClient.invalidateQueries(['GET_WORKFLOW']);
      scheduleInvalidate();
    },
    [scheduleInvalidate]
  );

  // Wire one listener per event. Each call goes through `useSignalRListener`
  // so environment-filtering is handled centrally.
  useSignalRListener(SIGNALR_LISTENERS.ON_EXTRACTION_STATUS_UPDATED, handlePeriodScopedEvent);
  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED,
    handlePeriodScopedEvent
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_STATUS_UPDATED, handlePeriodScopedEvent);
  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED,
    handlePeriodScopedEvent
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, handlePeriodScopedEvent);
  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED,
    handlePeriodScopedEvent
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_FILE_PROCESSING_UPDATED, handlePeriodScopedEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_FUND_EXTRACTION_UPDATED, handlePeriodScopedEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED, handlePeriodScopedEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_APPROVAL_STATUS_UPDATED, handlePeriodScopedEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_WORKFLOW_PERIOD_UPDATED, handleWorkflowPeriodEvent);
};
